import React from 'react';

const BONUS_LABELS = {
  joiningBonus: 'Joining Bonus',
  retentionBonus: 'Retention Bonus',
  referralBonus: 'Referral Bonus',
  specialAllowance: 'Special Allowance',
};

function formatMonth(monthStr) {
  if (!monthStr) return '';
  const [y, m] = monthStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[parseInt(m, 10) - 1] + ' ' + y;
}

export default function SalaryTab({
  selectedUser, selectedMonth, salaryState, workingDaysOfMonth,
  showThirdColumn, loading, saving, sampleData,
  isCurrentMonth, getBonusForMonth, sumComponentsToBePaid, sumBonusesToBePaid,
  getBackendValue, updateBonusField, updateComponentValue,
  generateSalary, saveSalaryStructure, savePayroll, printSalarySlip,
  prevMonth, nextMonth, payrollStatusForMonth, setSalaryState,
}) {
  if (!selectedUser) return null;

  const baseSalary = Number(salaryState?.baseSalary || 0);
  const perDay = (baseSalary / 26).toFixed(2);
  const bonusTotal = sumBonusesToBePaid(salaryState?.bonuses);
  const compTotal = sumComponentsToBePaid(salaryState?.components);
  const netPay = salaryState?.totalSalary || Math.round(baseSalary + bonusTotal + compTotal);

  return (
    <div>
      {/* Month Navigator */}
      <div className="pr-month-nav">
        <button className="pr-month-btn" onClick={prevMonth}>&larr;</button>
        <span className="pr-month-label">{formatMonth(selectedMonth)}</span>
        <button className="pr-month-btn" onClick={nextMonth}>&rarr;</button>
      </div>

      {/* KPI Cards */}
      <div className="pr-kpi-row">
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Base Salary</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{baseSalary.toLocaleString()}</div>
        </div>
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Bonuses</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{Math.round(bonusTotal).toLocaleString()}</div>
        </div>
        <div className="pr-kpi-card">
          <div className="pr-kpi-card-label">Working Days</div>
          <div className="pr-kpi-card-value">{workingDaysOfMonth}</div>
        </div>
        <div className="pr-kpi-card highlight">
          <div className="pr-kpi-card-label">Net Pay</div>
          <div className="pr-kpi-card-value">{'\u20B9'}{netPay.toLocaleString()}</div>
        </div>
      </div>

      {/* Status messages */}
      {payrollStatusForMonth() === 'beforeJoining' && (
        <div className="pr-card" style={{ textAlign: 'center', color: '#E17055' }}>
          Cannot generate payroll for months before joining date.
        </div>
      )}

      {payrollStatusForMonth() === 'exists' && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <span className="pr-status-badge generated">Payroll Exists</span>
        </div>
      )}

      {loading && <div className="pr-loading">Loading / calculating...</div>}

      {/* Bonuses Card */}
      <div className="pr-card">
        <div className="pr-card-header">Bonuses / One-time Items</div>
        {['joiningBonus', 'retentionBonus', 'referralBonus', 'specialAllowance'].map(function (bk) {
          const bobj = (salaryState?.bonuses && salaryState.bonuses[bk]) ? salaryState.bonuses[bk] : { amount: 0, mode: 'one-time', toBePaid: 0 };
          const toBePaidShown = Math.round(getBonusForMonth(bobj));

          return (
            <div className="pr-bonus-row" key={bk}>
              <div className="pr-bonus-label">{BONUS_LABELS[bk]}</div>
              <input
                className="pr-input pr-bonus-input"
                type="number"
                value={bobj.amount || 0}
                disabled={!isCurrentMonth(selectedMonth)}
                onChange={function (e) { updateBonusField(bk, 'amount', Number(e.target.value || 0)); }}
              />
              <select
                className="pr-select pr-bonus-select"
                value={bobj.mode || 'one-time'}
                disabled={!isCurrentMonth(selectedMonth)}
                onChange={function (e) { updateBonusField(bk, 'mode', e.target.value); }}
              >
                <option value="one-time">One-time</option>
                <option value="monthly">Monthly (÷12)</option>
                <option value="quarterly">Quarterly (÷4)</option>
                <option value="bi-annual">Bi-Annual (÷2)</option>
                <option value="annual">Annual</option>
              </select>
              <div className="pr-bonus-payable">
                <span className="pr-bonus-payable-chip">{'\u20B9'}{toBePaidShown}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Components Table */}
      <div className="pr-card">
        <div className="pr-card-header">Components</div>
        <table className="pr-comp-table">
          <thead>
            <tr>
              <th style={{ width: '35%' }}>Component</th>
              <th style={{ width: '20%' }}>Package</th>
              {showThirdColumn && (
                <>
                  <th style={{ width: '20%' }}>Working</th>
                  <th style={{ width: '25%' }}>To Be Paid</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Base Salary Row */}
            <tr className="pr-row-base">
              <td style={{ fontWeight: 700 }}>Base Salary</td>
              <td>
                <input
                  className="pr-input"
                  type="number"
                  value={baseSalary}
                  disabled={!isCurrentMonth(selectedMonth)}
                  onChange={function (e) {
                    setSalaryState(function (prev) {
                      return { ...prev, baseSalary: Number(e.target.value || 0) };
                    });
                  }}
                  style={{ width: '100%' }}
                />
              </td>
              {showThirdColumn && (
                <>
                  <td style={{ color: '#636e72', fontSize: '0.78rem' }}>26 days</td>
                  <td style={{ fontWeight: 700 }}>{'\u20B9'}{baseSalary}</td>
                </>
              )}
            </tr>
            {/* Per Day Rate */}
            <tr>
              <td style={{ color: '#636e72', fontSize: '0.82rem', paddingLeft: 20 }}>Per Day Rate (÷ 26)</td>
              <td>
                <input className="pr-input" type="number" value={perDay} readOnly style={{ width: '100%' }} />
              </td>
              {showThirdColumn && (
                <>
                  <td></td>
                  <td style={{ color: '#636e72', fontSize: '0.78rem' }}>auto-used below</td>
                </>
              )}
            </tr>

            {/* Component Rows */}
            {sampleData.map(function (comp) {
              const id = comp.id;
              const compEntry = salaryState?.components?.[id];
              const val = compEntry && typeof compEntry === 'object'
                ? Number(compEntry.amount ?? 0)
                : compEntry ?? comp.defaultAmount;

              const backendValue = getBackendValue(salaryState?.backendData, comp.backendField);
              const isReadOnly = comp.markReadOnly;
              const toBePaid = compEntry && typeof compEntry === 'object'
                ? Number(compEntry.toBePaid ?? 0)
                : 0;

              const isDeduction = toBePaid < 0;

              return (
                <tr key={id}>
                  <td>{comp.label}</td>
                  <td>
                    <input
                      className="pr-input"
                      type="number"
                      value={val}
                      disabled={!isCurrentMonth(selectedMonth)}
                      readOnly={isReadOnly}
                      onChange={function (e) { updateComponentValue(comp.id, Number(e.target.value || 0)); }}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {showThirdColumn && (
                    <>
                      <td>
                        <input className="pr-input" type="number" value={backendValue} readOnly style={{ width: '100%' }} />
                      </td>
                      <td className={isDeduction ? 'pr-val-ded' : (toBePaid > 0 ? 'pr-val-earn' : '')}>
                        {'\u20B9'}{Math.round(toBePaid)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Strip */}
      <div className="pr-summary">
        <div className="pr-summary-row">
          <span className="pr-summary-label">Base Salary</span>
          <span className="pr-summary-value">{'\u20B9'}{baseSalary.toLocaleString()}</span>
        </div>
        <div className="pr-summary-row">
          <span className="pr-summary-label">Components Total</span>
          <span className="pr-summary-value">{'\u20B9'}{Math.round(compTotal).toLocaleString()}</span>
        </div>
        <div className="pr-summary-row">
          <span className="pr-summary-label">Bonuses Total</span>
          <span className="pr-summary-value">{'\u20B9'}{Math.round(bonusTotal).toLocaleString()}</span>
        </div>
        <div className="pr-summary-row total">
          <span className="pr-summary-label">Net Payable</span>
          <span className="pr-summary-value">{'\u20B9'}{netPay.toLocaleString()}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pr-actions">
        <button className="pr-btn teal" onClick={saveSalaryStructure} disabled={loading}>
          Save Structure
        </button>
        <button className="pr-btn blue" onClick={generateSalary} disabled={loading}>
          {payrollStatusForMonth() === 'exists' ? 'Regenerate' : 'Generate'}
        </button>
        <button className="pr-btn violet" onClick={savePayroll} disabled={saving || loading || !showThirdColumn}>
          {saving ? 'Saving...' : 'Save Payroll'}
        </button>
        <button className="pr-btn coral" onClick={printSalarySlip} disabled={!showThirdColumn}>
          Print Slip
        </button>
      </div>
    </div>
  );
}
